import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2, CreditCard, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";

const FEATURES = {
  starter: ["Online menu & QR code", "Inventory tracking", "Daily sales reports", "Employee scheduling & time clock", "Customer database", "Up to 3 staff accounts"],
  professional: ["Everything in Starter", "AI sales & inventory forecasting", "Advanced inventory & food cost", "Loyalty & marketing tools", "Multi-location management", "Up to 10 staff accounts"],
};

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [params, setParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);

  const sub = user?.subscription;

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);

  const poll = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) { setVerifying(false); toast.error("Payment status check timed out."); return; }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setVerifying(false);
        toast.success("Payment successful! Your plan is now active.");
        await refreshUser();
        setParams({});
        return;
      }
      if (data.status === "expired") { setVerifying(false); toast.error("Payment session expired."); setParams({}); return; }
      setTimeout(() => poll(sessionId, attempts + 1), 2000);
    } catch (err) { setVerifying(false); toast.error(apiErr(err)); }
  }, [refreshUser, setParams]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) { setVerifying(true); poll(sid); }
  }, [params, poll]);

  const upgrade = async (planId) => {
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(apiErr(err)); setLoadingPlan(null); }
  };

  return (
    <div data-testid="billing-page">
      <PageHeader title="Billing & Plans" subtitle="Manage your subscription." />

      {verifying && (
        <div className="bg-accent/40 border border-accent rounded-xl p-4 mb-6 flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-sm">Verifying your payment...</p>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-lg bg-muted flex items-center justify-center">
            <CreditCard className="h-5 w-5 text-primary" />
          </span>
          <div>
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Current plan</p>
            <p className="font-heading text-2xl font-semibold capitalize">{sub?.plan}</p>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {sub?.status === "trial"
            ? <span>Free trial · <span className="font-semibold text-foreground">{sub?.trial_days_left ?? 14} days left</span></span>
            : <span className="capitalize">Status: <span className="font-semibold text-secondary">{sub?.status}</span></span>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {plans.map((p) => {
          const isCurrent = sub?.status === "active" && sub?.plan === p.id;
          const featured = p.id === "professional";
          return (
            <div key={p.id} data-testid={`billing-plan-${p.id}`}
              className={`rounded-2xl p-7 flex flex-col ${featured ? "bg-foreground text-[#FDFBF7]" : "bg-card border border-border"}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">{p.name}</h3>
                {featured && <Sparkles className="h-5 w-5 text-accent" />}
              </div>
              <p className={`text-sm mt-1 ${featured ? "text-[#FDFBF7]/70" : "text-muted-foreground"}`}>{p.tagline}</p>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-heading text-4xl font-semibold">${p.price}</span>
                <span className={`mb-1.5 text-sm ${featured ? "text-[#FDFBF7]/70" : "text-muted-foreground"}`}>/month</span>
              </div>
              <ul className="mt-6 space-y-2.5 text-sm flex-1">
                {FEATURES[p.id].map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className={`h-4 w-4 mt-0.5 shrink-0 ${featured ? "text-accent" : "text-secondary"}`} />
                    <span className={featured ? "text-[#FDFBF7]/90" : "text-foreground/80"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`upgrade-${p.id}-btn`}
                disabled={isCurrent || loadingPlan === p.id}
                onClick={() => upgrade(p.id)}
                className={`mt-6 ${featured ? "bg-primary hover:bg-[#B35A42] text-primary-foreground" : "bg-foreground hover:bg-foreground/90 text-[#FDFBF7]"}`}>
                {loadingPlan === p.id ? <Loader2 className="h-4 w-4 animate-spin" />
                  : isCurrent ? "Current plan" : `Upgrade to ${p.name}`}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
